import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
	Typography,
	Box,
	CircularProgress,
	useMediaQuery,
} from '@material-ui/core';
import GenerateTabs from '../../GenerateTabs';
import { colors } from '../../../../../src/theme/color-palette.js';
import PropTypes from 'prop-types';
import { convertToInternationalCurrencySystem } from '../../../../helper/convertAmount';
import {
	NETSALES,
	UNITS,
	CHANNEL_BREAKDOWN,
	chartMetricType,
	DATA_NOT_AVAILABLE,
	ERROR_SCREEN_MSG_STORE,
} from '../../../../constants/constant';
import DownloadTrend from '../../../../components/Download/DownloadTrend';
import { getChannelName, isEmptyObject } from './utils';
import DefinitionDrawer from '../../../../components/Definition/index';
import { getChartType } from '../../../../components/Definition';
import ErrorScreenMessage from '../../../../components/ErrorScreenMessage/ErrorScreenMessage.js';
import ShimmerCB from '../../../../components/ShimmerUI/ShimmerCB';
import CBTable from './CBTable.js';
import { getHeaders } from './utils';

const { FONT_COLOR } = colors;

const useStyles = makeStyles((theme) => ({
	container: {
		width: '100%',
		height: (P) => (P.height ? '548px' : '422px'),
	},
	loaderContainer: {
		height: '422px',
		display: 'flex',
	},
	circularProgress: {
		margin: 'auto',
	},
	chartHeader: {
		height: 'auto',
		padding: '16px 10px 4px 16px',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
	},
	chartTitle: {
		textAlign: 'left',
		fontSize: '18px',
		[theme.breakpoints.down('sm')]: {
			fontSize: '16px',
		},
		color: FONT_COLOR,
		fontFamily: 'Bogle-Regular',
		cursor: 'pointer',
	},
}));

const ChannelBreakdown = ({
	selectedFilter,
	data = {},
	updatedWidth,
	weekFilter,
	category,
	height,
	itemDrillDown,
}) => {
	const classes = useStyles({
		height,
	});
	const [tabValue, setTabValue] = useState(0);
	const isMediumScreen = useMediaQuery('(max-width:550px)');
	const isloading = data?.isloading[tabValue];
	const metricName = chartMetricType[tabValue];
	const chartdata = data[metricName];

	const initialCxt = {
		isDrawerOpened: false,
		metricType: null,
	};

	const [definition, setDefinition] = useState(initialCxt);

	const opendrawer = () => {
		setDefinition({
			isDrawerOpened: true,
			metricType: getChartType(CHANNEL_BREAKDOWN),
		});
	};

	const parseData = (data) => {
		const prefix = tabValue === 0 ? '$' : '';
		return data?.map((item) => ({
			...item,
			labelName: getChannelName(item.name || item.channelName, isMediumScreen),
			tyValue: item.tyValue,
			lyValue: item.lyValue,
			yoy: item.yoy,
			absYoY: item.absYoY,
			yoySign: item.yoy === -999999 ? 0 : item.yoy < 0 ? -1 : 1,
			absYoYSign: item.absYoY === -999999 ? 0 : item.absYoY < 0 ? -1 : 1,
			labelNameExcel: getChannelName(item.name || item.channelName),
			tyValueExcel: item.tyValue,
			lyValueExcel: item.lyValue,
			yoyExcel: item.yoy === -999999 ? 'NA' : item.yoy,
			absYoYExcel: item.absYoY === -999999 ? 'NA' : item.absYoY,
			tyValueTooltip:
				prefix + convertToInternationalCurrencySystem(item.tyValue),
			lyValueTooltip:
				prefix + convertToInternationalCurrencySystem(item.lyValue),
		}));
	};

	const myComponent = (
		<CBTable
			chartData={parseData(chartdata?.data)}
			headers={getHeaders(tabValue)}
			height={height}
		/>
	);

	const maxValue = Math.max(
		data?.summary?.netsalesTotal,
		data?.summary?.salesVolumeTotal
	);

	let extension = '';
	if (maxValue > 999999) {
		extension = 'm';
	} else if (maxValue > 9999) {
		extension = 'k';
	} else {
		extension = false;
	}

	const labels = [
		{
			tabName: NETSALES,
			tabValue:
				data?.summary?.netsalesTotal &&
				`$${convertToInternationalCurrencySystem(
					data?.summary?.netsalesTotal,
					extension
				)}`,
			active: tabValue === 0 ? true : false,
		},
		{
			tabName: UNITS,
			tabValue:
				data?.summary?.salesVolumeTotal &&
				convertToInternationalCurrencySystem(
					data?.summary?.salesVolumeTotal,
					extension
				),
			active: tabValue === 1 ? true : false,
		},
	];

	let errorComponent = null;
	// Creates the Error message according to the error code
	if (data?.isError?.includes(204)) {
		errorComponent = (
			<Box className={classes.container}>
				<ErrorScreenMessage
					chartTitle={CHANNEL_BREAKDOWN}
					errorMsg={DATA_NOT_AVAILABLE}
					showTitleInContent={true}
				/>
			</Box>
		);
	} else if (data?.isError?.some((el) => el >= 400)) {
		errorComponent = (
			<Box className={classes.container}>
				<ErrorScreenMessage
					chartTitle={CHANNEL_BREAKDOWN}
					errorMsg={ERROR_SCREEN_MSG_STORE}
					showTitleInContent={true}
				/>
			</Box>
		);
	}

	// Set the component to Channel breakdown, or to Error component according to the data & error component
	let mainComponent = null;
	const { cbSalesData = {}, cbUnitsData = {} } = data;
	if (
		Object.keys(cbSalesData).length === 0 &&
		Object.keys(cbUnitsData).length === 0 &&
		errorComponent
	) {
		mainComponent = errorComponent;
	} else {
		mainComponent = (
			<>
				<Box className={classes.chartHeader}>
					<Typography className={classes.chartTitle} onClick={opendrawer}>
						{CHANNEL_BREAKDOWN}
					</Typography>
					<DefinitionDrawer
						isOpen={definition.isDrawerOpened}
						metricType={definition.metricType}
						onClose={() => setDefinition(initialCxt)}
					/>
					{chartdata && !isEmptyObject(chartdata) ? (
						<Typography className={classes.download}>
							<DownloadTrend
								data={{ ...chartdata, data: parseData(chartdata?.data) }}
								chartTitle={CHANNEL_BREAKDOWN}
								weekFilter={weekFilter}
								category={category}
								selectedFilter={selectedFilter}
								isTabsTrend={true}
								headers={getHeaders(tabValue)}
							/>
						</Typography>
					) : null}
				</Box>
				<GenerateTabs
					createTabs={labels}
					childComponent={myComponent}
					setTabValue={setTabValue}
					tabValue={tabValue}
					updatedWidth={updatedWidth}
					data={data}
				/>
			</>
		);
	}

	return (
		<>
			<Box data-test-id="channel-breakdown-chart">
				{isloading ? (
					!itemDrillDown ? (
						<ShimmerCB />
					) : (
						<Box className={classes.loaderContainer}>
							<CircularProgress className={classes.circularProgress} />
						</Box>
					)
				) : (
					mainComponent
				)}
			</Box>
		</>
	);
};

ChannelBreakdown.defaultProps = {
	updatedWidth: null,
	itemDrillDown: false,
};

ChannelBreakdown.propTypes = {
	selectedFilter: PropTypes.string,
	data: PropTypes.shape({}),
	updatedWidth: PropTypes.number,
	weekFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	category: PropTypes.string,
	height: PropTypes.string,
	itemDrillDown: PropTypes.bool,
};

export default ChannelBreakdown;
